import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import type { TFunction } from 'i18next';
import { when } from 'mobx';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';

import {
  BLACKBAUD_CLIENT_ID,
  type BlackbaudRaisersEdgeIntegration,
  type BlackbaudRaisersEdgeIntegrations,
  type Fundraiser,
  type IBlackbaudFund,
} from '@feathr/blackbox';
import {
  Alert,
  AlertType,
  Button,
  ButtonValid,
  CardV2 as Card,
  Checkbox,
  Fieldset,
  FormElement,
  type IModalStep,
  Input,
  List,
  Radios,
  showError,
  Text,
  toast,
  Toggle,
} from '@feathr/components';
import type { TModalSize } from '@feathr/components/src/Modal/Modal';
import BBFundSelect from '@feathr/extender/App/Settings/Account/RaisersEdgeIntegrationPage/RaisersEdgeSyncWizard/RaisersEdgeSyncActivity/BBFundSelect';
import { cssVar } from '@feathr/hooks';

import { logos } from './assets/helpers';

interface IProps {
  fundraiser?: Fundraiser;
  t: TFunction;
  BlackbaudRaisersEdgeIntegrations: BlackbaudRaisersEdgeIntegrations;
  accountId: string;
}

/**
 * @param fundraiser - The fundraiser model being used in the CRM connection process.
 *
 * When the back button is clicked on the ready to enable integration step set the
 * ready_to_enable_integration flag to false so that the user can re-enable it if they go forward again.
 */
function handleOnBackReadyToEnableIntegration(fundraiser: Fundraiser): void {
  const crm = fundraiser.get('crm') ?? {};
  fundraiser.set({
    crm: {
      ...crm,
      ready_to_enable_integration: false,
      credential_check_passed: false,
    },
  });
}

/**
 * Handle the "Check CRM" button click by validating the CRM credentials.
 * @param fundraiser - The fundraiser model being used in the CRM connection process.
 * @param t - The translation function.
 */
async function handleCheckCRM(fundraiser: Fundraiser, t: TFunction): Promise<void> {
  try {
    const crm = fundraiser.get('crm');
    await fundraiser?.checkCRM({ crm });
    fundraiser.set({
      crm: {
        ...crm,
        credential_check_passed: true,
      },
    });
    toast(t('CRM credentials are valid'), { type: 'success' });
  } catch (error) {
    showError(error, t);
  }
}

function getSalesforceSteps(
  baseProps: Partial<IModalStep>,
  fundraiser: Fundraiser,
  t: TFunction,
): IModalStep[] {
  return [
    {
      ...baseProps,
      title: t('Provide Salesforce Credentials'),
      description: (
        <Trans t={t}>
          To connect to Salesforce,{' '}
          <a
            href={
              'https://help.feathr.co/hc/en-us/articles/36120664372375-How-to-Connect-Feathr-Fundraising-with-Salesforce'
            }
            rel={'noopener noreferrer'}
            target={'_blank'}
          >
            follow these instructions
          </a>{' '}
          to create an external client app and then provide its client ID, client secret, and your
          Salesforce domain.
        </Trans>
      ),
      content: (
        <Flex direction={'column'} gap={cssVar('--spacing-4')}>
          <FormElement>
            <Fieldset>
              <Input
                attribute={['crm', 'client_id']}
                helpText={t('Sometimes called "Client ID"')}
                isClearable={true}
                label={t('Consumer Key')}
                model={fundraiser}
                type={'password'}
              />
              <Input
                attribute={['crm', 'client_secret']}
                helpText={t('Sometimes called "Client Secret"')}
                isClearable={true}
                label={t('Consumer Secret')}
                model={fundraiser}
                type={'password'}
              />
              <Input
                attribute={['crm', 'domain']}
                helpText={t('The domain name of your Salesforce without the domain suffix.')}
                isClearable={true}
                label={t('Domain Name')}
                model={fundraiser}
                suffix={'.salesforce.com'}
              />
            </Fieldset>
          </FormElement>
          <ButtonValid
            errors={
              fundraiser.validate(['crm.client_id', 'crm.client_secret', 'crm.domain'], false)
                .errors
            }
            isFullWidth={true}
            onClick={(): Promise<void> => handleCheckCRM(fundraiser, t)}
            tooltipPosition={'bottom'}
            type={'primary'}
          >
            {t('Check user credentials')}
          </ButtonValid>
        </Flex>
      ),
      nextStepDisabled:
        fundraiser.validate(
          ['crm.client_id', 'crm.client_secret', 'crm.domain', 'crm.credential_check_passed'],
          false,
        ).errors?.length > 0,
      errors: fundraiser.validate(
        ['crm.client_id', 'crm.client_secret', 'crm.domain', 'crm.credential_check_passed'],
        false,
        'grouped',
      ).errors,
    },
    {
      ...baseProps,
      title: t('Sync Donations to Salesforce NPSP'),
      description: t(
        'Feathr creates complete donor records and donation opportunities in Salesforce automatically.',
      ),
      content: (
        <Flex direction={'column'} gap={cssVar('--spacing-2')}>
          <Text.DefaultSemibold>{t('What syncs:')}</Text.DefaultSemibold>
          <List hasBullets={true}>
            <List.Item>{t('Contact and Household Account (matched by name + email)')}</List.Item>
            <List.Item>
              {t('Opportunity with donation amount, date, and transaction details')}
            </List.Item>
            <List.Item>{t('Payment reference and acknowledgment tracking')}</List.Item>
          </List>
          <Text.DefaultSemibold>{t('How it works:')}</Text.DefaultSemibold>
          <List hasBullets={true}>
            <List.Item>
              {t('Existing donors: New Opportunity added to current Contact/Account')}
            </List.Item>
            <List.Item>
              {t('New donors: New Contact, Household Account, and Opportunity created')}
            </List.Item>
            <List.Item>{t('All donations marked "Closed Won" and "To Be Acknowledged"')}</List.Item>
          </List>
          <Text.DefaultRegular>
            {t('Your Salesforce stays current with every donation—zero manual work required.')}
          </Text.DefaultRegular>
          <Flex pt={cssVar('--spacing-2')}>
            <Checkbox
              attribute={['crm', 'ready_to_enable_integration']}
              label={t("I'm ready to enable the integration.")}
              model={fundraiser}
            />
          </Flex>
        </Flex>
      ),
      completeButtonText: t('Connect'),
      nextStepDisabled:
        fundraiser.validate(['crm.ready_to_enable_integration'], false).errors?.length > 0 ||
        !fundraiser.get('crm')?.ready_to_enable_integration,
      errors: fundraiser.validate(['crm.ready_to_enable_integration'], false, 'grouped').errors,
      onBack: () => handleOnBackReadyToEnableIntegration(fundraiser),
    },
  ];
}

interface IBlackbaudStepContentProps {
  fundraiser: Fundraiser;
  t: TFunction;
}

function BlackbaudStepContent({
  fundraiser,
  t,
}: Readonly<IBlackbaudStepContentProps>): React.JSX.Element {
  const [confirmed, setConfirmed] = useState(false);

  function handleConfirm(): void {
    setConfirmed(true);
    const crm = fundraiser.get('crm') ?? {};

    fundraiser.set({
      crm: {
        ...crm,
        ready_to_enable_integration: true,
      },
    });
  }

  return (
    <Flex direction={'column'} gap={cssVar('--spacing-3')}>
      <Text.DefaultRegular>
        {t("Follow these steps to add Feathr to your Raiser's Edge NXT:")}
      </Text.DefaultRegular>
      <ol>
        <li>{t("Log in to your Raiser's Edge NXT admin account")}</li>
        <li>{t('Navigate to Control Panel → Applications')}</li>
        <li>{t('Click Add Application')}</li>
        <li>{t('Search for "Feathr" and select it from the marketplace')}</li>
        <li>{t('Click Install and approve the requested permissions')}</li>
      </ol>
      <Button
        href={`https://app.blackbaud.com/marketplace/applications/${BLACKBAUD_CLIENT_ID}?action=getstarted`}
        suffix={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
        target={'_blank'}
        type={'link'}
      >
        {t('Application marketplace')}
      </Button>
      {confirmed ? (
        <Alert type={AlertType.success}>
          <Text.DefaultSemibold>{t('Feathr application successfully added!')}</Text.DefaultSemibold>
        </Alert>
      ) : (
        <Button buttonShape={'pill'} isFullWidth={true} onClick={handleConfirm} type={'secondary'}>
          {t("I've added the Feathr Application")}
        </Button>
      )}
    </Flex>
  );
}

interface IConnectBlackbaudStepContentProps {
  t: TFunction;
  BlackbaudRaisersEdgeIntegrations: BlackbaudRaisersEdgeIntegrations;
  fundraiser: Fundraiser;
  accountId: string;
}

function ConnectBlackbaudStepContent({
  t,
  BlackbaudRaisersEdgeIntegrations,
  fundraiser,
}: Readonly<IConnectBlackbaudStepContentProps>): React.JSX.Element {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState<{
    given_name: string;
    family_name: string;
    email: string;
  } | null>(null);

  // Check if already connected (BlackbaudOAuthCallback handles the OAuth code before redirecting here)
  React.useEffect(() => {
    async function checkConnection(): Promise<void> {
      setIsVerifying(true);
      try {
        // Clear cache to ensure fresh data after OAuth redirect
        BlackbaudRaisersEdgeIntegrations.clearApiCache();
        const listResponse = BlackbaudRaisersEdgeIntegrations.list();
        await when(() => !listResponse.isPending);

        const integration = listResponse.models.length > 0 ? listResponse.models[0] : undefined;

        if (integration?.isConnected) {
          const user = integration.get('authenticated_user');
          setIsConnected(true);
          setAuthenticatedUser(user || null);
          const crm = fundraiser.get('crm') ?? {};

          fundraiser.set({
            crm: {
              ...crm,
              // @ts-expect-error - This field is used for step validation only
              ready_to_connect: true,
            },
          });
        }
      } finally {
        setIsVerifying(false);
      }
    }

    checkConnection();
  }, [BlackbaudRaisersEdgeIntegrations, fundraiser]);

  async function handleConnectToBlackbaud(): Promise<void> {
    setIsConnecting(true);
    try {
      const listResponse = BlackbaudRaisersEdgeIntegrations.list();
      await when(() => !listResponse.isPending);

      let integration = listResponse.models.length > 0 ? listResponse.models[0] : undefined;

      if (!integration) {
        const redirectUri = `${globalThis.location.origin}/oauth/blackbaud`;
        const newIntegration = BlackbaudRaisersEdgeIntegrations.create();
        integration = await BlackbaudRaisersEdgeIntegrations.add(newIntegration, {
          validate: false,
          redirectUri,
        });

        if (integration?.isErrored) {
          toast(t('Failed to create Blackbaud integration. Please try again.'), { type: 'error' });
          return;
        }

        BlackbaudRaisersEdgeIntegrations.clearApiCache();
      }

      // Construct OAuth URL with all required params
      const loginUrl = new URL('https://app.blackbaud.com/oauth/authorize');
      loginUrl.searchParams.set('client_id', BLACKBAUD_CLIENT_ID);
      loginUrl.searchParams.set('response_type', 'code');
      loginUrl.searchParams.set('redirect_uri', `${globalThis.location.origin}/oauth/blackbaud`);

      // Add state parameter with encoded return path to step 3 (sync toggle)
      // Use /fundraise/settings route which will redirect to /fundraise with preserved params
      const returnPath = `${globalThis.location.pathname}/settings?modal=crm&step=3`;
      loginUrl.searchParams.set('state', btoa(returnPath));

      globalThis.location.assign(loginUrl.toString());
    } catch (error) {
      showError(error, t);
    } finally {
      setIsConnecting(false);
    }
  }

  function renderConnectionStatus(): React.JSX.Element {
    if (isVerifying) {
      return (
        <Alert type={AlertType.info}>
          <Flex align={'center'} gap={cssVar('--spacing-2')}>
            <Text.DefaultMedium>{t('Verifying connection...')}</Text.DefaultMedium>
          </Flex>
        </Alert>
      );
    }

    if (isConnected) {
      return (
        <Alert type={AlertType.success}>
          <Flex direction={'column'} gap={cssVar('--spacing-2')}>
            <Text.DefaultSemibold>{t('Successfully connected to Blackbaud!')}</Text.DefaultSemibold>
            {authenticatedUser && (
              <Text.DefaultRegular>
                {t('Connected as: {{name}} ({{email}})', {
                  name: `${authenticatedUser.given_name} ${authenticatedUser.family_name}`,
                  email: authenticatedUser.email,
                })}
              </Text.DefaultRegular>
            )}
          </Flex>
        </Alert>
      );
    }

    return (
      <>
        <Button
          buttonShape={'pill'}
          isFullWidth={true}
          isLoading={isConnecting}
          loadingText={t('Connecting...')}
          onClick={handleConnectToBlackbaud}
          prefix={<img alt={t('Blackbaud logo')} height={20} src={logos.blackbaud} width={20} />}
          type={'secondary'}
        >
          {t('Connect to Blackbaud')}
        </Button>
        <Alert type={AlertType.info}>
          <Text.DefaultMedium>
            {t(
              'Your credentials are securely handled by Blackbaud. Feathr never sees your password.',
            )}
          </Text.DefaultMedium>
        </Alert>
      </>
    );
  }

  return (
    <Flex direction={'column'} gap={cssVar('--spacing-3')}>
      <Text.DefaultRegular>
        {t('Connect your Blackbaud account to complete the integration.')}
      </Text.DefaultRegular>
      <Text.DefaultRegular>
        {t(
          "You'll be redirected to Blackbaud to sign in and authorize Feathr to access your Raiser's Edge NXT data.",
        )}
      </Text.DefaultRegular>

      {renderConnectionStatus()}
    </Flex>
  );
}

interface IEnableDonationSyncStepContentProps {
  fundraiser: Fundraiser;
  t: TFunction;
  BlackbaudRaisersEdgeIntegrations: BlackbaudRaisersEdgeIntegrations;
}

function EnableDonationSyncStepContent({
  fundraiser,
  t,
  BlackbaudRaisersEdgeIntegrations,
}: Readonly<IEnableDonationSyncStepContentProps>): React.JSX.Element {
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [selectedFund, setSelectedFund] = useState<IBlackbaudFund | undefined>();
  const [integration, setIntegration] = useState<BlackbaudRaisersEdgeIntegration | null>(null);

  React.useEffect(() => {
    async function loadIntegration(): Promise<void> {
      const listResponse = BlackbaudRaisersEdgeIntegrations.list();
      await when(() => !listResponse.isPending);
      const bbIntegration = listResponse.models[0];

      setIntegration(bbIntegration);

      if (bbIntegration) {
        const upsyncActivities = bbIntegration.get('upsync_activities');
        const isEnabled = upsyncActivities?.donations || false;
        const fundId = bbIntegration.get('fundraising')?.fund_id;
        setSyncEnabled(isEnabled);

        if (fundId) {
          setSelectedFund({ id: fundId, description: '' });
        }

        const crm = fundraiser.get('crm') ?? {};
        fundraiser.set({
          crm: {
            ...crm,
            // @ts-expect-error - used for step validation only
            fundraising_donation_enabled: isEnabled,
          },
        });
      }
    }

    loadIntegration();
  }, [BlackbaudRaisersEdgeIntegrations, fundraiser]);

  async function handleToggleSync(): Promise<void> {
    const newValue = !syncEnabled;
    setSyncEnabled(newValue);

    if (integration) {
      try {
        // Patch upsync_activities for donation sync (Feathr → BB)
        // Preserve existing upsync_activities values
        const existingUpsync = integration.get('upsync_activities') || {};
        await integration.patch({
          upsync_activities: {
            ...existingUpsync,
            donations: newValue,
          },
        });
        // Clear cache so settings page shows fresh data
        BlackbaudRaisersEdgeIntegrations.clearApiCache();
        const crm = fundraiser.get('crm') ?? {};
        fundraiser.set({
          crm: {
            ...crm,
            // @ts-expect-error - used for step validation only
            fundraising_donation_enabled: newValue,
          },
        });
      } catch (error) {
        showError(error, t);
        setSyncEnabled(!newValue);
      }
    }
  }

  async function handleFundChange(fund: IBlackbaudFund | undefined): Promise<void> {
    setSelectedFund(fund);

    if (integration && fund) {
      try {
        await integration.patch({
          fundraising: {
            fund_id: fund.id,
          },
        });
        BlackbaudRaisersEdgeIntegrations.clearApiCache();
      } catch (error) {
        showError(error, t);
      }
    }
  }

  return (
    <Flex direction={'column'} gap={cssVar('--spacing-4')}>
      <Card theme={'disabled'}>
        <Card.Content>
          <Flex align={'center'} gap={cssVar('--spacing-4')} justify={'space-between'} w={'100%'}>
            <Flex direction={'column'} gap={cssVar('--spacing-1')}>
              <Text.DefaultSemibold>{t('Sync donations as gifts')}</Text.DefaultSemibold>
              <Text.DefaultRegular>
                {t("Automatically send donation data from Feathr to Raiser's Edge NXT")}
              </Text.DefaultRegular>
            </Flex>
            <Toggle onChange={handleToggleSync} value={syncEnabled} />
          </Flex>
        </Card.Content>
      </Card>
      {syncEnabled && integration && (
        <>
          <BBFundSelect
            helpText={t('Donations earned through Feathr will be assigned to the selected fund.')}
            integration={integration}
            label={t('Fund')}
            onChange={handleFundChange}
            value={selectedFund?.id}
          />
          <Button
            href={'https://help.feathr.co/hc/en-us/articles/21160663599255'}
            target={'_blank'}
            type={'link'}
          >
            {t('Learn more about donation syncing')}
          </Button>
          <Alert type={AlertType.info}>
            <Flex direction={'column'} gap={cssVar('--spacing-2')}>
              <Text.DefaultSemibold>{t('Want to sync all data?')}</Text.DefaultSemibold>
              <Text.DefaultRegular>
                {t(
                  "This integration can also sync constituent data from Raiser's Edge NXT to Feathr and help manage constituent subscription status.",
                )}
              </Text.DefaultRegular>
              <Button href={'/settings/integrations/raisers-edge'} type={'link'}>
                {t("Manage Raiser's Edge NXT Integration")}
              </Button>
            </Flex>
          </Alert>
        </>
      )}
    </Flex>
  );
}

function getBlackbaudSteps(
  baseProps: Partial<IModalStep>,
  fundraiser: Fundraiser,
  t: TFunction,
  BlackbaudRaisersEdgeIntegrations: BlackbaudRaisersEdgeIntegrations,
  accountId: string,
): IModalStep[] {
  return [
    {
      ...baseProps,
      title: t('Add Feathr Application'),
      description: t("Step 1 of 3: Set up the Feathr application in Raiser's Edge NXT."),
      content: <BlackbaudStepContent fundraiser={fundraiser} t={t} />,
      leftActions: [],
      nextStepDisabled:
        fundraiser.validate(['crm.ready_to_enable_integration'], false).errors?.length > 0 ||
        !fundraiser.get('crm')?.ready_to_enable_integration,
      onBack: () => handleOnBackReadyToEnableIntegration(fundraiser),
    },
    {
      ...baseProps,
      title: t('Connect Blackbaud Account'),
      description: t('Step 2 of 3: Authenticate your Blackbaud account'),
      content: (
        <ConnectBlackbaudStepContent
          accountId={accountId}
          BlackbaudRaisersEdgeIntegrations={BlackbaudRaisersEdgeIntegrations}
          fundraiser={fundraiser}
          t={t}
        />
      ),
      leftActions: [],
      // @ts-expect-error - ready_to_connect is set dynamically for step validation
      nextStepDisabled: !fundraiser.get('crm')?.ready_to_connect,
    },
    {
      ...baseProps,
      allowOverflow: true,
      title: t('Enable Donation Sync'),
      description: t("Configure donation syncing to Raiser's Edge NXT"),
      content: (
        <EnableDonationSyncStepContent
          BlackbaudRaisersEdgeIntegrations={BlackbaudRaisersEdgeIntegrations}
          fundraiser={fundraiser}
          t={t}
        />
      ),
      completeButtonText: t('Complete Setup'),
      leftActions: [],
    },
  ];
}

function getCRMConnectSteps({
  fundraiser,
  t,
  BlackbaudRaisersEdgeIntegrations,
  accountId,
}: IProps): IModalStep[] {
  if (!fundraiser) {
    return [];
  }

  const baseProps: Partial<IModalStep> = {
    size: 'default' as TModalSize,
    nextStepDisabled: false,
    leftActions: [],
  };

  const steps: IModalStep[] = [
    {
      size: 'default',
      title: t('Connect your CRM'),
      description: t('Select your CRM platform and authenticate to begin the integration process.'),
      content: (
        <Radios
          attribute={['crm', '_cls']}
          layout={'block'}
          model={fundraiser}
          options={[
            { name: 'Salesforce', id: 'SalesforceCRM', image: logos.salesforce },
            { name: "Blackbaud Raiser's Edge NXT", id: 'BlackbaudCRM', image: logos.blackbaud },
          ]}
          orientation={'horizontal'}
        />
      ),
      leftActions: [],
      nextStepDisabled: fundraiser.validate(['crm._cls'], false).errors?.length > 0,
    },
  ];
  if (fundraiser.get('crm')?._cls === 'SalesforceCRM') {
    steps.push(...getSalesforceSteps(baseProps, fundraiser, t));
  } else if (fundraiser.get('crm')?._cls === 'BlackbaudCRM') {
    steps.push(
      ...getBlackbaudSteps(baseProps, fundraiser, t, BlackbaudRaisersEdgeIntegrations, accountId),
    );
  } else {
    steps.push({
      size: 'default',
      title: '',
      description: '',
      content: <div />,
      nextStepDisabled: true,
    });
  }
  return steps;
}

export default getCRMConnectSteps;
